import {
  ANALYTICS_EVENTS,
  posthog,
  registerAnalyticsContext,
  unregisterAnalyticsContext,
  type CommChannel
} from '@kiku/analytics/client';
import type { ScreeningOutcome } from '@kiku/config/constants';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { InferResponseType } from 'hono/client';
import { Check, ChevronDown, Copy, ExternalLink, Loader2, MoreHorizontal } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ActivityTimeline } from '~/components/features/applications/activity-timeline';
import { AtsPushStatus } from '~/components/features/applications/ats-push-status';
import { CandidateProfileTab } from '~/components/features/candidates/candidate-profile-tab';
import { DepartmentBreadcrumb } from '~/components/features/departments/department-breadcrumb';
import { AssessmentResults } from '~/components/features/scoring/assessment-results';
import { EligibilityResults } from '~/components/features/scoring/eligibility-results';
import { ExpectationsResult } from '~/components/features/scoring/expectations-result';
import { InformationResults } from '~/components/features/scoring/information-results';
import { NiceToHaveResults } from '~/components/features/scoring/nice-to-have-results';
import { ScoreOverviewCard } from '~/components/features/scoring/score-overview-card';
import { TranscriptView } from '~/components/features/scoring/transcript-view';
import { PageContainer } from '~/components/layouts/page-container';
import { buttonVariants } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '~/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '~/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { useDocumentTitle } from '~/hooks/use-document-title';
import { useSlugIds } from '~/hooks/use-slug-ids';
import { api } from '~/lib/api';
import { meQueryOptions } from '~/lib/auth';
import { formatPhone } from '~/lib/format-phone';
import { i18n } from '~/lib/i18n';
import { resolveSlugIds } from '~/lib/slug-resolver';
import { cn } from '~/lib/utils';
import { jobDetailQueryOptions } from '~/queries/jobs.queries';

type ScoringResponse = InferResponseType<
  (typeof api.api.org)[':orgId']['department'][':deptId']['jobs'][':jobId']['applications'][':applicationId']['scoring']['$get']
>;

type TranscriptResponse = InferResponseType<
  (typeof api.api.org)[':orgId']['department'][':deptId']['jobs'][':jobId']['applications'][':applicationId']['transcript']['$get']
>;

type InterviewResponse = InferResponseType<
  (typeof api.api.org)[':orgId']['department'][':deptId']['jobs'][':jobId']['applications'][':applicationId']['interview']['$get']
>;

type ApplicationsResponse = InferResponseType<
  (typeof api.api.org)[':orgId']['department'][':deptId']['jobs'][':jobId']['applications']['$get']
>;
type ApplicationItem = ApplicationsResponse['applications'][number];

function scoringQueryOptions(orgId: string, deptId: string, jobId: string, applicationId: string) {
  return queryOptions({
    queryKey: ['scoring', orgId, deptId, jobId, applicationId] as const,
    queryFn: async (): Promise<ScoringResponse> => {
      const response = await api.api.org[':orgId'].department[':deptId'].jobs[
        ':jobId'
      ].applications[':applicationId'].scoring.$get({
        param: { orgId, deptId, jobId, applicationId }
      });
      if (!response.ok) throw new Error(i18n.t('errors.unexpected'));
      return response.json();
    }
  });
}

function transcriptQueryOptions(
  orgId: string,
  deptId: string,
  jobId: string,
  applicationId: string
) {
  return queryOptions({
    queryKey: ['transcript', orgId, deptId, jobId, applicationId] as const,
    queryFn: async (): Promise<TranscriptResponse> => {
      const response = await api.api.org[':orgId'].department[':deptId'].jobs[
        ':jobId'
      ].applications[':applicationId'].transcript.$get({
        param: { orgId, deptId, jobId, applicationId }
      });
      if (!response.ok) throw new Error(i18n.t('errors.unexpected'));
      return response.json();
    }
  });
}

function interviewQueryOptions(
  orgId: string,
  deptId: string,
  jobId: string,
  applicationId: string
) {
  return queryOptions({
    queryKey: ['interview', orgId, deptId, jobId, applicationId] as const,
    queryFn: async (): Promise<InterviewResponse> => {
      const response = await api.api.org[':orgId'].department[':deptId'].jobs[
        ':jobId'
      ].applications[':applicationId'].interview.$get({
        param: { orgId, deptId, jobId, applicationId }
      });
      if (!response.ok) throw new Error(i18n.t('errors.unexpected'));
      return response.json();
    }
  });
}

function applicationsQueryOptions(orgId: string, deptId: string, jobId: string) {
  return queryOptions({
    queryKey: ['applications', orgId, deptId, jobId] as const,
    queryFn: async () => {
      const response = await api.api.org[':orgId'].department[':deptId'].jobs[
        ':jobId'
      ].applications.$get({
        param: { orgId, deptId, jobId },
        query: {}
      });
      if (!response.ok) throw new Error(i18n.t('errors.unexpected'));
      return response.json();
    }
  });
}

export const Route = createFileRoute(
  '/_authenticated/org/$orgSlug/department/$deptSlug/jobs/$jobId/$applicationId/'
)({
  loader: ({ context, params }) => {
    const { orgId, deptId } = resolveSlugIds(context.queryClient, params.orgSlug, params.deptSlug);
    if (orgId && deptId) {
      void context.queryClient.prefetchQuery(
        scoringQueryOptions(orgId, deptId, params.jobId, params.applicationId)
      );
      void context.queryClient.prefetchQuery(
        interviewQueryOptions(orgId, deptId, params.jobId, params.applicationId)
      );
    }
  },
  component: ApplicationDetailPage
});

function ApplicationDetailPage(): React.ReactNode {
  const { t } = useTranslation();
  const { orgSlug, deptSlug, jobId, applicationId } = Route.useParams();
  const { orgId, deptId } = useSlugIds(orgSlug, deptSlug);
  const queryClient = useQueryClient();
  const { data: session } = useQuery(meQueryOptions);

  useEffect(() => {
    if (!deptId) return;
    registerAnalyticsContext({ deptId, jobId, applicationId });
    return () => {
      unregisterAnalyticsContext(['deptId', 'jobId', 'applicationId']);
    };
  }, [deptId, jobId, applicationId]);

  const { data: jobData } = useQuery({
    ...jobDetailQueryOptions(orgId, deptId ?? '', jobId),
    enabled: !!orgId && !!deptId
  });

  const { data: applicationsData } = useQuery({
    ...applicationsQueryOptions(orgId, deptId ?? '', jobId),
    enabled: !!orgId && !!deptId
  });

  const application = applicationsData?.applications.find(
    (a: ApplicationItem) => a.id === applicationId
  );
  useDocumentTitle(
    [application?.candidate.name, jobData?.job.title].filter(Boolean).join(' | ') || undefined
  );
  const stages = applicationsData?.stages ?? [];
  const currentStage = stages.find((s) => s.id === application?.stageId);
  const stageNames = useMemo(() => new Map(stages.map((s) => [s.id, s.name])), [stages]);

  const { data: scoringData, isLoading: scoringLoading } = useQuery({
    ...scoringQueryOptions(orgId, deptId ?? '', jobId, applicationId),
    enabled: !!orgId && !!deptId,
    refetchInterval: (query) => {
      const score = query.state.data?.score;
      if (!score || score.status === 'pending' || score.status === 'processing') {
        return 5000;
      }
      return false;
    }
  });

  const { data: transcriptData } = useQuery({
    ...transcriptQueryOptions(orgId, deptId ?? '', jobId, applicationId),
    enabled: !!orgId && !!deptId && scoringData?.score?.status === 'completed'
  });

  const hasCopyPasteDetected =
    transcriptData?.messages.some((m) => m.role !== 'assistant' && m.copyPasteDetected) ?? false;

  const { data: interviewData } = useQuery({
    ...interviewQueryOptions(orgId, deptId ?? '', jobId, applicationId),
    enabled: !!orgId && !!deptId
  });

  const score = scoringData?.score;

  const stageChangeMutation = useMutation({
    mutationFn: async (stageId: string) => {
      const response = await api.api.org[':orgId'].department[':deptId'].jobs[
        ':jobId'
      ].applications[':applicationId'].stage.$patch({
        param: { orgId, deptId: deptId ?? '', jobId, applicationId },
        json: { stageId }
      });
      if (!response.ok) throw new Error(i18n.t('errors.unexpected'));
      return response.json();
    },
    onMutate: async (stageId) => {
      const queryKey = ['applications', orgId, deptId ?? '', jobId] as const;
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ApplicationsResponse>(queryKey);
      const fromStageId =
        previous?.applications.find((a) => a.id === applicationId)?.stageId ?? null;
      if (previous) {
        queryClient.setQueryData(queryKey, {
          ...previous,
          applications: previous.applications.map((a) =>
            a.id === applicationId ? { ...a, stageId } : a
          )
        });
      }
      return { previous, fromStageId };
    },
    onSuccess: (_result, stageId, context) => {
      posthog.capture(ANALYTICS_EVENTS.CANDIDATE_STAGE_CHANGED, {
        application_id: applicationId,
        from_stage_id: context.fromStageId,
        to_stage_id: stageId,
        source: 'detail_page'
      });
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['applications', orgId, deptId ?? '', jobId], context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ['applications', orgId, deptId ?? '', jobId]
      });
      void queryClient.invalidateQueries({
        queryKey: ['activity', orgId, deptId ?? '', jobId, applicationId]
      });
    }
  });

  const handleStageChange = useCallback(
    (stageId: string | null) => {
      if (stageId) stageChangeMutation.mutate(stageId);
    },
    [stageChangeMutation]
  );

  const interviewUrl = interviewData?.interview
    ? `${window.location.origin}/interview/${interviewData.interview.token}`
    : null;
  const [copied, setCopied] = useState(false);
  const [commChannel, setCommChannel] = useState<CommChannel>('manual');
  const handleCopy = useCallback(async () => {
    if (!interviewUrl) return;
    const url =
      commChannel !== 'manual' ? `${interviewUrl}?comm_channel=${commChannel}` : interviewUrl;
    await navigator.clipboard.writeText(url);
    posthog.capture(ANALYTICS_EVENTS.INTERVIEW_LINK_COPIED, { comm_channel: commChannel });
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  }, [interviewUrl, commChannel]);

  const questionScores = score?.questionScores ?? [];
  const eligibilityQuestions = questionScores.filter((q) => q.category === 'eligibility');
  const niceToHaveQuestions = questionScores.filter((q) => q.category === 'nice_to_have');
  const expectationsQuestion = questionScores.find((q) => q.category === 'job_preview');
  const assessmentQuestions = questionScores.filter((q) => q.category === 'assessment');
  const informationQuestions = questionScores.filter((q) => q.category === 'information');

  const stagePicker = application && (
    <Select value={application.stageId} onValueChange={handleStageChange}>
      <SelectTrigger className="sm:w-44">
        <SelectValue className="flex-none sm:flex-1">
          <div
            className="size-2.5 rounded-full sm:hidden"
            style={{ backgroundColor: currentStage?.color ?? undefined }}
          />
          <div className="hidden min-w-0 items-center gap-1.5 sm:flex">
            <div
              className="size-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: currentStage?.color ?? undefined }}
            />
            <span className="truncate text-sm font-medium">{currentStage?.name}</span>
          </div>
        </SelectValue>
      </SelectTrigger>
      <SelectContent alignItemWithTrigger={false} className="min-w-44">
        {stages.map((stage) => (
          <SelectItem key={stage.id} value={stage.id}>
            <div className="flex items-center gap-2">
              {stage.color && (
                <div className="size-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
              )}
              {stage.name}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const interviewActions = interviewUrl && !score && (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger
          render={
            <DropdownMenuTrigger
              className={cn(
                buttonVariants(),
                'w-8 px-0 bg-transparent text-foreground hover:bg-muted',
                'sm:w-auto sm:px-2.5 sm:bg-primary sm:text-primary-foreground sm:hover:bg-primary/90'
              )}
            />
          }
        >
          <MoreHorizontal className="size-4 sm:hidden" />
          <span className="hidden sm:inline">{t('applications.detail.interviewLink')}</span>
          <ChevronDown className="hidden sm:inline size-4" />
        </TooltipTrigger>
        <TooltipContent side="bottom" className="sm:hidden">
          {t('applications.detail.interviewLink')}
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" className="min-w-56">
        <div className="px-2 py-1.5">
          <p className="mb-1.5 text-xs font-medium text-muted-foreground">
            {t('applications.detail.commChannel.label')}
          </p>
          <div className="flex gap-1">
            {(['manual', 'email', 'sms', 'whatsapp'] as const).map((ch) => (
              <button
                key={ch}
                type="button"
                onClick={() => {
                  setCommChannel(ch);
                }}
                className={cn(
                  'flex-1 rounded px-1.5 py-1 text-xs transition-colors',
                  commChannel === ch
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                )}
              >
                {t(`applications.detail.commChannel.${ch}`)}
              </button>
            ))}
          </div>
        </div>
        <DropdownMenuItem
          onClick={() => {
            void handleCopy();
          }}
        >
          {copied ? <Check className="size-4 text-green-600" /> : <Copy className="size-4" />}
          {copied ? t('applications.detail.linkCopied') : t('applications.detail.copyLink')}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            window.open(interviewUrl, '_blank');
          }}
        >
          <ExternalLink className="size-4" />
          {t('applications.detail.openInterview')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <PageContainer
      breadcrumbs={[
        { content: <DepartmentBreadcrumb orgSlug={orgSlug} deptSlug={deptSlug} /> },
        {
          label: t('jobs.title'),
          href: `/org/${orgSlug}/department/${deptSlug}/jobs`
        },
        {
          label: jobData?.job.title ?? '',
          href: `/org/${orgSlug}/department/${deptSlug}/jobs/${jobId}`
        },
        { label: application?.candidate.name ?? '' }
      ]}
      title={application?.candidate.name ?? ''}
      subtitle={
        application
          ? [
              application.candidate.email,
              application.candidate.phoneNumber
                ? formatPhone(
                    application.candidate.phoneNumber,
                    application.candidate.phoneCountryCode
                  )
                : null
            ]
              .filter(Boolean)
              .join(' · ')
          : ''
      }
      badge={stagePicker}
      actions={
        <div className="flex items-center gap-2">
          {deptId && session?.role === 'admin' && (
            <DropdownMenu>
              <DropdownMenuTrigger
                className={cn(buttonVariants({ variant: 'outline' }), 'gap-1.5')}
              >
                {t('jobs.applicantSync.adminActions')}
                <ChevronDown className="size-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="p-3 w-72">
                <AtsPushStatus
                  orgId={orgId}
                  deptId={deptId}
                  jobId={jobId}
                  applicationId={applicationId}
                  isAdmin={true}
                  scoringCompleted={score?.status === 'completed'}
                  variant="inline"
                />
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {interviewActions}
        </div>
      }
    >
      <Tabs defaultValue="application">
        <TabsList variant="line" className="w-full sm:w-fit sm:self-start">
          <TabsTrigger
            value="application"
            className="data-active:text-primary data-active:after:bg-primary"
          >
            {t('applications.detail.tabs.application')}
          </TabsTrigger>
          <TabsTrigger
            value="profile"
            className="data-active:text-primary data-active:after:bg-primary"
          >
            {t('applications.detail.tabs.profile')}
          </TabsTrigger>
          <TabsTrigger
            value="activity"
            className="data-active:text-primary data-active:after:bg-primary"
          >
            {t('applications.detail.tabs.activity')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="application" className="flex flex-col gap-6 pt-4">
          {/* Scoring states */}
          {scoringLoading && (
            <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-6">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{t('scoring.pending')}</span>
            </div>
          )}

          {score && (score.status === 'pending' || score.status === 'processing') && (
            <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-6">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{t('scoring.processing')}</span>
            </div>
          )}

          {score?.status === 'failed' && (
            <div className="rounded-lg border border-destructive bg-card p-6">
              <p className="text-sm text-destructive">{t('scoring.failed')}</p>
              {score.errorMessage && (
                <p className="mt-1 text-xs text-muted-foreground">{score.errorMessage}</p>
              )}
            </div>
          )}

          {/* Scoring results */}
          {score?.status === 'completed' &&
            score.overallScore !== null &&
            score.outcome &&
            score.aiSummary && (
              <div className="flex flex-col gap-6">
                <ScoreOverviewCard
                  overallScore={score.overallScore}
                  outcome={score.outcome as ScreeningOutcome}
                  aiSummary={score.aiSummary}
                  hasCopyPasteDetected={hasCopyPasteDetected}
                />

                <EligibilityResults
                  questions={eligibilityQuestions}
                  orgId={orgId}
                  deptId={deptId ?? ''}
                  jobId={jobId}
                  applicationId={applicationId}
                />

                {expectationsQuestion && (
                  <ExpectationsResult
                    questionScoreId={expectationsQuestion.id}
                    questionId={expectationsQuestion.questionId}
                    questionText={expectationsQuestion.questionText}
                    verdict={expectationsQuestion.verdict}
                    reasoning={expectationsQuestion.reasoning}
                    evidence={expectationsQuestion.evidence}
                    orgId={orgId}
                    deptId={deptId ?? ''}
                    jobId={jobId}
                    applicationId={applicationId}
                  />
                )}

                <NiceToHaveResults
                  questions={niceToHaveQuestions}
                  orgId={orgId}
                  deptId={deptId ?? ''}
                  jobId={jobId}
                  applicationId={applicationId}
                />

                <AssessmentResults
                  questions={assessmentQuestions}
                  orgId={orgId}
                  deptId={deptId ?? ''}
                  jobId={jobId}
                  applicationId={applicationId}
                />

                <InformationResults questions={informationQuestions} />

                {transcriptData?.messages && (
                  <TranscriptView
                    messages={transcriptData.messages}
                    questionScores={questionScores}
                  />
                )}
              </div>
            )}
        </TabsContent>

        <TabsContent value="profile" className="pt-4">
          {orgId && application?.candidate.id && (
            <CandidateProfileTab orgId={orgId} candidateId={application.candidate.id} />
          )}
        </TabsContent>

        <TabsContent value="activity" className="pt-4">
          {deptId && (
            <ActivityTimeline
              orgId={orgId}
              deptId={deptId}
              jobId={jobId}
              applicationId={applicationId}
              stageNames={stageNames}
            />
          )}
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}
